import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, EMPTY, mergeMap, Observable, throwError } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthRedirectService } from '../core/services/auth-redirect.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private baseApiUrl: string = environment.API_BASE_URL;

  constructor(
    private auth: AuthService,
    private authRedirect: AuthRedirectService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const excludedUrls = [
      `${this.baseApiUrl}/customer/onboard`,
      `${this.baseApiUrl}/customer/complete-onboarding`,
      `${this.baseApiUrl}/onboarding/verify/*`,
      `${this.baseApiUrl}/onboarding/complete/*`,
      `${this.baseApiUrl}/pentest/request-public`,
    ];

    if (excludedUrls.some((url) => req.url.includes(url))) {
      return next.handle(req);
    }

    return this.auth.getAccessTokenSilently().pipe(
      // Positioned BEFORE mergeMap on purpose: catchError only sees errors from upstream
      // of itself, so this handles a failure to obtain a token and nothing else. Below the
      // mergeMap it would also catch every error from the request itself, and since this
      // interceptor sits outside ErrorInterceptor, any rethrown API error — a 403 from a
      // plan gate, a 400, a 500 — would send the user to log in.
      catchError((err) => {
        // Couldn't obtain a valid access token for a protected request — the Auth0
        // session is gone/expired (login_required), a silent refresh timed out, etc.
        // Send the user to log in and get a fresh token instead of failing silently.
        if (this.authRedirect.redirectToLogin()) {
          return EMPTY; // navigating away to Auth0
        }
        return throwError(() => new Error(err));
      }),
      mergeMap((token) => {
        const clonedReq = req.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
        });
        return next.handle(clonedReq);
      }),
    );
  }
}
